import React from 'react';
import PropTypes from 'prop-types';

const Blood = ({ searchTerm, isDarkMode }) => {
  const bloodValues = {
    cbc: [
      {
        test: 'Hematocrit',
        conventional: {
          male: '41%–53%',
          female: '36%–46%'
        },
        si: {
          male: '0.41–0.53',
          female: '0.36–0.46'
        }
      },
      {
        test: 'Hemoglobin',
        conventional: {
          male: '13.5–17.5 g/dL',
          female: '12.0–16.0 g/dL'
        },
        si: {
          male: '135–175 g/L',
          female: '120–160 g/L'
        }
      },
      {
        test: 'Mean Corpuscular Hemoglobin (MCH)',
        conventional: '25–35 pg/cell',
        si: '0.39–0.54 fmol/cell'
      },
      {
        test: 'Mean Corpuscular Hemoglobin Conc. (MCHC)',
        conventional: '31%–36% Hb/cell',
        si: '4.8–5.6 mmol Hb/L'
      },
      {
        test: 'Mean Corpuscular Volume (MCV)',
        conventional: '80–100 μm³',
        si: '80–100 fL'
      }
    ],
    bloodVolume: [
      {
        test: 'Plasma Volume',
        conventional: {
          male: '25–43 mL/kg',
          female: '28–45 mL/kg'
        },
        si: {
          male: '0.025–0.043 L/kg',
          female: '0.028–0.045 L/kg'
        }
      },
      {
        test: 'Red Cell Volume',
        conventional: {
          male: '20–36 mL/kg',
          female: '19–31 mL/kg'
        },
        si: {
          male: '0.020–0.036 L/kg',
          female: '0.019–0.031 L/kg'
        }
      }
    ],
    wbc: [
      {
        test: 'Total Leukocyte Count (WBC)',
        conventional: '4500–11,000/mm³',
        si: '4.5–11.0 × 10⁹/L'
      },
      {
        test: 'Neutrophils, Segmented',
        conventional: '54%–62%',
        si: '0.54–0.62'
      },
      {
        test: 'Neutrophils, Bands',
        conventional: '3%–5%',
        si: '0.03–0.05'
      },
      {
        test: 'Lymphocytes',
        conventional: '25%–33%',
        si: '0.25–0.33'
      },
      {
        test: 'Monocytes',
        conventional: '3%–7%',
        si: '0.03–0.07'
      },
      {
        test: 'Eosinophils',
        conventional: '1%–3%',
        si: '0.01–0.03'
      },
      {
        test: 'Basophils',
        conventional: '0%–0.75%',
        si: '0.00–0.0075'
      }
    ],
    platelets: [
      {
        test: 'Platelet Count',
        conventional: '150,000–400,000/mm³',
        si: '150–400 × 10⁹/L'
      }
    ],
    coagulation: [
      {
        test: 'Partial Thromboplastin Time (PTT)',
        conventional: '25–40 seconds',
        si: '25–40 seconds'
      },
      {
        test: 'Prothrombin Time (PT)',
        conventional: '11–15 seconds',
        si: '11–15 seconds'
      },
      {
        test: 'D-Dimer',
        conventional: '≤250 ng/mL',
        si: '≤1.4 nmol/L'
      }
    ],
    other: [
      {
        test: 'Reticulocyte Count',
        conventional: '0.5%–1.5%',
        si: '0.005–0.015'
      },
      {
        test: 'Erythrocyte Count',
        conventional: {
          male: '4.3–5.9 million/mm³',
          female: '3.5–5.5 million/mm³'
        },
        si: {
          male: '4.3–5.9 × 10¹²/L',
          female: '3.5–5.5 × 10¹²/L'
        }
      },
      {
        test: 'Erythrocyte Sedimentation Rate (ESR)',
        conventional: {
          male: '0–15 mm/h',
          female: '0–20 mm/h'
        },
        si: {
          male: '0–15 mm/h',
          female: '0–20 mm/h'
        }
      },
      {
        test: 'CD4+ T-Lymphocyte Count',
        conventional: '≥500/mm³',
        si: '≥0.5 × 10⁹/L'
      },
      {
        test: 'Troponin I',
        conventional: '≤0.04 ng/mL',
        si: '≤0.04 μg/L'
      },
      {
        test: 'Hemoglobin A1c',
        conventional: '≤6%',
        si: '≤42 mmol/mol'
      }
    ]
  };

  // Add search filter function
  const filterData = (data, term) => {
    if (!term) return data;
    const lowerTerm = term.toLowerCase();
    
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = data[key].filter(item => 
        item.test.toLowerCase().includes(lowerTerm) ||
        (typeof item.conventional === 'string' && 
         item.conventional.toLowerCase().includes(lowerTerm)) ||
        (typeof item.si === 'string' && 
         item.si.toLowerCase().includes(lowerTerm)) ||
        (item.conventional?.male?.toLowerCase().includes(lowerTerm)) ||
        (item.conventional?.female?.toLowerCase().includes(lowerTerm))
      );
      return acc;
    }, {});
  };

  const filteredValues = filterData(bloodValues, searchTerm);

  return (
    <div className={isDarkMode ? 'ext_lbs_dark-mode' : ''}>
      {/* Remove search bar since it's in parent */}
      {filteredValues.cbc.length > 0 && (
        <>
          <h2>Complete Blood Count (CBC)</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.cbc.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>
                      {typeof item.conventional === 'string' 
                        ? item.conventional 
                        : `Male: ${item.conventional.male}\nFemale: ${item.conventional.female}`}
                    </td>
                    <td>
                      {typeof item.si === 'string'
                        ? item.si
                        : `Male: ${item.si.male}\nFemale: ${item.si.female}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.bloodVolume.length > 0 && (
        <>
          <h2>Blood Volume</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.bloodVolume.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{`Male: ${item.conventional.male}\nFemale: ${item.conventional.female}`}</td>
                    <td>{`Male: ${item.si.male}\nFemale: ${item.si.female}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.wbc.length > 0 && (
        <>
          <h2>White Blood Cells</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.wbc.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.platelets.length > 0 && (
        <>
          <h2>Platelets and Coagulation</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.platelets.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.coagulation.length > 0 && (
        <>
          <h2>Platelets and Coagulation</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.coagulation.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.other.length > 0 && (
        <>
          <h2>Other Hematologic Parameters</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.other.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>
                      {typeof item.conventional === 'string' 
                        ? item.conventional 
                        : `Male: ${item.conventional.male}\nFemale: ${item.conventional.female}`}
                    </td>
                    <td>
                      {typeof item.si === 'string'
                        ? item.si
                        : `Male: ${item.si.male}\nFemale: ${item.si.female}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

Blood.propTypes = {
  searchTerm: PropTypes.string,
  isDarkMode: PropTypes.bool
};

export default Blood;
